import { Component, effect, HostListener, input, output, signal } from '@angular/core';
import { Media, MediaContainer } from '@app/core/models/atomics.model';
import { DEVICE_SIZE, mediaConfig } from '@app/core/models/deviceSize.model';
import { DeviceService } from '@shared/tools/device.service';
import { WindowRef } from '@shared/tools/window';

@Component({
  selector: 'cx-media',
  templateUrl: './media.component.html',
  styleUrls: ['./media.component.scss'],
  imports: [],
})
export class MediaComponent {
  media = input.required<Media | MediaContainer>();
  width = input('');
  height = input('');

  mediaLoad = output<void>();
  mediaError = output<Event>();

  currentMedia = signal<Media | null>(null);

  constructor(
    private windowRef: WindowRef,
    private deviceService: DeviceService,
  ) {
    effect(() => {
      if (this.windowRef.isBrowser()) {
        this.updateMedia(this.media());
      }
    });
  }

  @HostListener('window:resize')
  protected onResize() {
    if (!this.windowRef.isBrowser()) return;
    this.updateMedia(this.media());
  }

  private updateMedia(media: Media | MediaContainer | null) {
    if (!media) {
      this.currentMedia.set(null);
      return;
    }

    const deviceSize = this.deviceService.getCurrentSize();

    if (this.isMediaContainer(media)) {
      const deviceMedia = media[deviceSize] ?? this.getMediaFallback(media);
      this.currentMedia.set(this.normalize(deviceMedia));
    } else {
      this.currentMedia.set(this.normalize(media));
    }
  }

  private normalize(media: Media | null): Media {
    return {
      src: media ? this.getMediaSrc(media) : '',
      altText: media?.altText ?? '',
    };
  }

  private getMediaFallback(container: MediaContainer): Media | null {
    const fallbackSize = Object.entries(mediaConfig.mediaFormats)
      .map(([size, config]) => ({ size: size as DEVICE_SIZE, width: config.width }))
      .sort((a, b) => b.width - a.width)
      .find(entry => {
        const media = container[entry.size];
        return media && media.src.trim() !== '';
      });

    return fallbackSize ? container[fallbackSize.size] : null;
  }

  private isMediaContainer(media: Media | MediaContainer): media is MediaContainer {
    return Object.values(DEVICE_SIZE).some(size => size in media);
  }

  private getMediaSrc(media: Media): string {
    if (!media?.src) return '';
    if (media.src.includes('/src/../assets')) {
      return media.src.match(/\/src\/.*$/)?.[0] || '';
    }

    return media.src;
  }

  onLoad(): void {
    this.mediaLoad.emit();
  }

  onError(event: Event): void {
    this.mediaError.emit(event);
  }
}
